import type { Schedule } from "@pilatestime/shared-types";
import { scheduleToLabelInputs } from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Building2, Calendar, Clock, Eye, Users } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchSchedulesByStaffMemberQueryOptions } from "@/api/schedules";
import { DataTable } from "@/components/data-table";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useScheduleLabel } from "@/hooks/useScheduleLabel";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/staff/$staffId/schedules",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, staffId } = params;

		// Preload schedules data
		context.queryClient.ensureQueryData(
			fetchSchedulesByStaffMemberQueryOptions(userId, token, orgId, staffId),
		);

		return { userId, token, orgId, staffId };
	},
	component: StaffSchedulesPage,
});

function StaffSchedulesPage() {
	const { t } = useTranslation();
	const { orgId, staffId } = Route.useParams();
	const { userId, token } = Route.useLoaderData();
	const { formatDate } = useDateFormatter();
	const formatScheduleLabel = useScheduleLabel();
	const navigate = useNavigate();

	// Fetch schedules data
	const { data: schedules } = useSuspenseQuery(
		fetchSchedulesByStaffMemberQueryOptions(userId, token, orgId, staffId),
	);

	const columns: ColumnDef<Schedule>[] = useMemo(
		() => [
			{
				accessorKey: "id",
				header: () => (
					<div className="flex items-center gap-2">
						<Calendar className="w-4 h-4" />
						{t("common.name")}
					</div>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div>
							<div className="font-medium">
								{formatScheduleLabel(scheduleToLabelInputs(schedule))}
							</div>
							{schedule.description && (
								<div className="text-sm text-base-content/70">
									{schedule.description}
								</div>
							)}
						</div>
					);
				},
			},
			{
				accessorKey: "establishment",
				header: () => (
					<div className="flex items-center gap-2">
						<Building2 className="w-4 h-4" />
						{t("common.establishment")}
					</div>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							{schedule.establishment?.name || "-"}
						</div>
					);
				},
			},
			{
				accessorKey: "type",
				header: t("common.type"),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							<span className="badge badge-outline">
								{t(`common.${schedule.type.toLowerCase()}`)}
							</span>
						</div>
					);
				},
			},
			{
				accessorKey: "time",
				header: () => (
					<div className="flex items-center gap-2">
						<Clock className="w-4 h-4" />
						{t("common.time")}
					</div>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							<div className="font-medium">
								{schedule.startTime} - {schedule.endTime}
							</div>
							<div className="text-sm text-base-content/70">
								{schedule.daysOfWeek
									.map((day) => t(`common.${day.toLowerCase()}`))
									.join(", ")}
							</div>
						</div>
					);
				},
			},
			{
				accessorKey: "enrollmentCount",
				header: () => (
					<div className="flex items-center gap-2">
						<Users className="w-4 h-4" />
						{t("common.enrollments")}
					</div>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							<span className="font-medium">
								{schedule.enrollmentCount || 0}
								{schedule.type === "GROUP" && schedule.capacity
									? ` / ${schedule.capacity}`
									: ""}
							</span>
						</div>
					);
				},
			},
			{
				accessorKey: "period",
				header: t("common.period"),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							<div className="font-medium">
								{formatDate(new Date(schedule.startDate).getTime())}
							</div>
							{schedule.endDate && (
								<div className="text-sm text-base-content/70">
									{t("common.to")}{" "}
									{formatDate(new Date(schedule.endDate).getTime())}
								</div>
							)}
						</div>
					);
				},
			},
			{
				id: "actions",
				header: t("common.actions"),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="flex justify-center">
							<Link
								to="/app/org/$orgId/schedules/$scheduleId"
								params={{
									orgId,
									scheduleId: schedule.id,
								}}
								className="btn btn-ghost btn-sm"
								title={t("common.view")}
							>
								<Eye className="w-4 h-4" />
								{t("common.view")}
							</Link>
						</div>
					);
				},
			},
		],
		[t, formatDate, orgId, formatScheduleLabel],
	);

	const handleRowClick = useCallback(
		(schedule: Schedule) => {
			navigate({
				to: "/app/org/$orgId/schedules/$scheduleId",
				params: { orgId, scheduleId: schedule.id },
			});
		},
		[navigate, orgId],
	);

	// Mobile (< sm) rendering: one card per schedule instead of a squeezed
	// table. Tapping the card opens the schedule details.
	const renderMobileCard = useCallback(
		(schedule: Schedule) => (
			<div className="p-3 space-y-1.5">
				<div className="flex items-start justify-between gap-2">
					<span className="font-medium leading-tight truncate">
						{formatScheduleLabel(scheduleToLabelInputs(schedule))}
					</span>
					<span className="badge badge-sm badge-outline whitespace-nowrap">
						{t(`common.${schedule.type.toLowerCase()}`)}
					</span>
				</div>
				<div className="text-xs text-base-content/60 flex flex-wrap items-center gap-x-2 gap-y-0.5">
					<span className="inline-flex items-center gap-1">
						<Clock className="w-3 h-3 shrink-0" />
						{schedule.startTime} - {schedule.endTime}
					</span>
					<span className="truncate">
						{schedule.daysOfWeek
							.map((day) => t(`common.${day.toLowerCase()}`))
							.join(", ")}
					</span>
				</div>
				<div className="text-xs text-base-content/60 flex flex-wrap items-center gap-x-2 gap-y-0.5">
					{schedule.establishment?.name && (
						<span className="inline-flex items-center gap-1 truncate">
							<Building2 className="w-3 h-3 shrink-0" />
							<span className="truncate">{schedule.establishment.name}</span>
						</span>
					)}
					<span className="inline-flex items-center gap-1">
						<Users className="w-3 h-3 shrink-0" />
						{schedule.enrollmentCount || 0}
						{schedule.type === "GROUP" && schedule.capacity
							? ` / ${schedule.capacity}`
							: ""}
					</span>
				</div>
			</div>
		),
		[t, formatScheduleLabel],
	);

	return (
		<div className="h-full flex flex-col space-y-6">
			<div className="h-full overflow-auto">
				<DataTable
					data={schedules}
					isLoading={false}
					error={null}
					columns={columns}
					testId="staff-schedules-table"
					canManage={false}
					onRowClick={handleRowClick}
					renderMobileCard={renderMobileCard}
					emptyState={{
						icon: Calendar,
						title: t("common.noSchedules"),
						description: t("common.noSchedulesDescription"),
					}}
				/>
			</div>
		</div>
	);
}
